import { type InfraService } from '@carsfinders/contracts';
import { Archive, ExternalLink, Pencil } from 'lucide-react';

import { t } from '../../../lib/i18n';
import { cn } from '../../clients/utils';
import { approx, billingLabel, formatDate, formatMoney } from '../format';

const TH = 'px-3 py-2 text-left font-mono text-[10px] font-normal tracking-[0.12em] text-cf-ink-subtle uppercase';
const TD = 'px-3 py-2.5 align-top text-[13px] text-cf-ink';
const ACTION =
  'inline-flex h-8 items-center gap-1 rounded-cf-md border border-cf-line bg-cf-surface px-2.5 text-[12px] font-semibold text-cf-ink hover:border-cf-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cf-ink';

/**
 * «Реєстр сервісів» — every service with its period, expected amount, next
 * date and reminder lead, plus «Редагувати» / «Архівувати». Archived rows show
 * only when «Показати архівні» is on, muted and without the archive action.
 */
export function InfraServicesTable({
  services,
  showArchived,
  onToggleArchived,
  onEdit,
  onArchive,
}: {
  services: readonly InfraService[];
  showArchived: boolean;
  onToggleArchived: (next: boolean) => void;
  onEdit: (service: InfraService) => void;
  onArchive: (service: InfraService) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <label className="inline-flex w-fit items-center gap-2 text-[13px] text-cf-ink-muted">
        <input
          type="checkbox"
          checked={showArchived}
          onChange={(event) => onToggleArchived(event.target.checked)}
          className="h-4 w-4 accent-[var(--color-cf-brand)]"
          data-testid="infra-show-archived"
        />
        {t('admin.infraCosts.services.showArchived')}
      </label>

      {services.length === 0 ? (
        <p className="py-6 text-center text-[13px] text-cf-ink-subtle">{t('admin.infraCosts.services.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse" data-testid="infra-services">
            <thead>
              <tr className="border-b border-cf-line">
                <th scope="col" className={TH}>{t('admin.infraCosts.services.col.name')}</th>
                <th scope="col" className={TH}>{t('admin.infraCosts.services.col.purpose')}</th>
                <th scope="col" className={TH}>{t('admin.infraCosts.services.col.period')}</th>
                <th scope="col" className={`${TH} text-right`}>{t('admin.infraCosts.services.col.amount')}</th>
                <th scope="col" className={TH}>{t('admin.infraCosts.services.col.nextDue')}</th>
                <th scope="col" className={TH}>{t('admin.infraCosts.services.col.remind')}</th>
                <th scope="col" className={TH}>
                  <span className="sr-only">{t('admin.infraCosts.services.col.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {services.map((service) => (
                <tr
                  key={service.id}
                  data-service-id={service.id}
                  data-active={service.active ? 'true' : 'false'}
                  className={cn('border-b border-cf-line-faint', !service.active && 'text-cf-ink-subtle')}
                >
                  <th scope="row" className={`${TD} text-left font-semibold`}>
                    <span className="flex flex-wrap items-center gap-1.5">
                      {service.name}
                      {!service.active && (
                        <span className="rounded-full bg-cf-fill px-2 py-0.5 text-[11px] font-semibold text-cf-ink-muted">
                          {t('admin.infraCosts.services.archived')}
                        </span>
                      )}
                    </span>
                    <span className="block text-[12px] font-normal text-cf-ink-subtle">
                      {[service.provider, t(`admin.infraCosts.category.${service.category}`)]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                    {service.billingUrl && (
                      <a
                        href={service.billingUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="mt-0.5 inline-flex items-center gap-1 text-[12px] font-normal text-cf-brand underline-offset-2 hover:underline"
                      >
                        {t('admin.infraCosts.services.billingLink')}
                        <ExternalLink size={12} aria-hidden="true" />
                      </a>
                    )}
                  </th>
                  <td className={`${TD} text-cf-ink-muted`}>{service.purpose || '—'}</td>
                  <td className={TD}>{billingLabel(service.billingKind, service.cadence)}</td>
                  <td className={`${TD} text-right font-mono`}>
                    {approx(formatMoney(service.amount, service.currency), service.amountIsEstimate)}
                  </td>
                  <td className={`${TD} font-mono`}>
                    {service.nextDueDate ? formatDate(service.nextDueDate) : t('admin.infraCosts.services.notScheduled')}
                  </td>
                  <td className={TD}>
                    {t('admin.infraCosts.services.remindDays', { days: String(service.remindDaysBefore) })}
                  </td>
                  <td className={`${TD} whitespace-nowrap`}>
                    <span className="flex justify-end gap-1.5">
                      <button
                        type="button"
                        className={ACTION}
                        onClick={() => onEdit(service)}
                        aria-label={t('admin.infraCosts.services.editAria', { service: service.name })}
                      >
                        <Pencil size={13} aria-hidden="true" />
                        {t('admin.infraCosts.services.edit')}
                      </button>
                      {service.active && (
                        <button
                          type="button"
                          className={ACTION}
                          onClick={() => onArchive(service)}
                          aria-label={t('admin.infraCosts.services.archiveAria', { service: service.name })}
                        >
                          <Archive size={13} aria-hidden="true" />
                          {t('admin.infraCosts.services.archive')}
                        </button>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
